import { CaretDownIcon, InfoIcon, PaperclipIcon, XIcon } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { toast } from 'sonner';

import { MomentArticlePicker } from './MomentArticlePicker';

import type { CalendarMomentPrefill } from '@/components/reach/editorial/article-promotion';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Confirm } from '@/components/ui/confirm';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  type CalendarMoment,
  type CalendarMomentAttachment,
  useCalendarMomentMutations,
} from '@/hooks/calendar/useCalendarMoments';
import {
  DESK_CONTENT_TYPES,
  DESK_CONTENT_TYPE_LABEL,
  type DeskContentType,
  isDeskContentType,
} from '@/lib/desk/content-type';
import { PRODUCTION_STAGE_LABEL, type ProductionStage } from '@/lib/desk/stage';
import {
  FIELD_LABEL,
  ITEM_TITLE,
  ROW_HOVER,
  SETTINGS_ROW,
  SETTINGS_ROW_DESCRIPTION,
  SETTINGS_ROW_TITLE,
  THUMBNAIL_SQUARE,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/** The sentinel for "the operator did not say". Radix Select forbids "". */
const NO_TYPE = '__none__';
const NO_STAGE = '__none__';
const PRODUCTION_STAGES: readonly ProductionStage[] = ['idea', 'drafting', 'made'];

interface CalendarMomentDialogProps {
  /**
   * `null` closed. A `CalendarMoment` edits that moment. A `"YYYY-MM-DD"`
   * string opens an empty editor starting on that day.
   *
   * One prop rather than `open` + `moment` + `defaultDate`, because those three
   * can express states this dialog does not have — open with no subject and no
   * date being the obvious one. A shape that cannot represent a nonsense state
   * does not need a guard against it.
   */
  subject: CalendarMoment | string | null;
  onClose: () => void;
  /**
   * Starting values for a NEW moment — "Add to calendar" on an editorial
   * article. Ignored when editing. Its `startsOn` wins over a day `subject`
   * only when it has one; an undated article still opens on the clicked day.
   */
  prefill?: CalendarMomentPrefill | null;
}

const toInput = (value: Date | undefined): string => (value ? format(value, 'yyyy-MM-dd') : '');
const fromInput = (value: string): Date | undefined => (value ? parseISO(value) : undefined);

/**
 * Create or edit a moment — the operator's own dated work on the Publishing
 * calendar. "Q3 blog goes live", "Film Mark's interview", "Booth build day".
 *
 * 🔴 THE TYPE FIELD IS OPTIONAL, AND IT OFFERS THE FOUNDER'S OWN SIX WORDS.
 *
 * This dialog shipped with NO type picker, deliberately, and that reasoning
 * still stands where it was aimed: the obvious version offers blog /
 * newsletter / interview / shoot / webinar, none of which appears in the
 * personas, the PRD, the Reach plans or the franchise spec. Shipping those
 * would be inventing a vocabulary and teaching it back to the person who has
 * to live with it. That is still forbidden.
 *
 * The original rule named its own reversal condition — "a sub-type earns a
 * field the day someone needs to FILTER by one" — and that day arrived:
 * `calendar_moments.content_type` was added 2026-09-07 and the Content Desk
 * reads it (`_build-payload.ts` -> `deriveContentType({ momentType })`) to
 * decide a card's label, its icon and which effort bucket it lands in.
 *
 * Nothing wrote it. So every moment reached a client's desk with
 * `momentType: null`, fell through every branch, and rendered as **"Proposal"**
 * — meaning "Nothing has been written yet — you are deciding whether it should
 * be" — printed under "Film Mark's interview". That is a false statement about
 * a filming day that is already booked.
 *
 * The options are `DESK_CONTENT_TYPES`, the founder's own vocabulary given
 * verbatim on 2026-09-06, so this satisfies the reversal condition without
 * violating the rule that produced it: no new words are invented here. It stays
 * OPTIONAL and defaults to nothing — "the operator did not say" is a real
 * answer, and a guessed default would be the invention this rule forbids.
 *
 * No colour picker either: colour on this calendar encodes the object CLASS,
 * not a per-item preference (`calendarItemTone.ts`). Founder, 2026-09-04:
 * _"people don't differentiate events like that."_
 */
export function CalendarMomentDialog({
  subject,
  onClose,
  prefill = null,
}: CalendarMomentDialogProps): JSX.Element {
  const existing = typeof subject === 'object' && subject !== null ? subject : null;
  const { create, update, remove, uploadAttachment, removeAttachment, getAttachmentUrl } =
    useCalendarMomentMutations();

  const [title, setTitle] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [visibleOnDesk, setVisibleOnDesk] = React.useState(true);
  const [attachment, setAttachment] = React.useState<CalendarMomentAttachment | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  /* Stable for the dialog's lifetime, not the moment's own id — a moment
     being CREATED has no id yet, so an attachment uploaded before the first
     save still needs somewhere to live. Reset alongside every other field
     below. */
  const momentKeyRef = React.useRef(existing?.id ?? crypto.randomUUID());
  /* Opens itself when editing a moment that already has either field, so an
     existing link is never hidden behind a closed disclosure the operator has
     no reason to suspect holds anything. */
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [startsOn, setStartsOn] = React.useState('');
  const [endsOn, setEndsOn] = React.useState('');
  /* Progressive disclosure, not a fixed Starts/Ends pair — most moments
     ("Q3 blog goes live") are one day. `false` shows a single "Date" field;
     `true` splits it into "Starts"/"Ends". Founder, 2026-09-09: "it shouldn't
     have start and end; it should just say date... some type of progressive
     disclosure, maybe where they would click 'Add an end date'." */
  const [showEndDate, setShowEndDate] = React.useState(false);
  const [contentType, setContentType] = React.useState<DeskContentType | null>(null);
  const [productionStage, setProductionStage] = React.useState<ProductionStage | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Reset when the SUBJECT changes, not on every open: the dialog is mounted
  // once by the calendar and re-pointed, so keying off `subject` is what makes
  // "edit A, close, edit B" show B's values rather than A's.
  React.useEffect(() => {
    if (subject === null) return;
    if (existing) {
      setTitle(existing.title);
      setNotes(existing.notes ?? '');
      setUrl(existing.url ?? '');
      /* Open it when there IS something behind it. A closed disclosure over a
         populated field is a value the operator cannot see and has no reason to
         look for — the same defect as a link stored in prose, one layer up. */
      setDetailsOpen(
        Boolean(existing.url?.trim() || existing.notes?.trim() || existing.attachment),
      );
      setStartsOn(existing.startsOn ?? '');
      setEndsOn(existing.endsOn ?? '');
      // Show the split only when there is a genuine range to show — a
      // single-day moment (`endsOn === startsOn`, or no end at all) stays
      // collapsed to one field.
      setShowEndDate(Boolean(existing.endsOn && existing.endsOn !== existing.startsOn));
      setContentType(existing.contentType);
      setProductionStage(existing.productionStage);
      setVisibleOnDesk(existing.visibleOnDesk);
      setAttachment(existing.attachment);
      momentKeyRef.current = existing.id;
      return;
    }
    setTitle('');
    setNotes('');
    setUrl('');
    setDetailsOpen(false);
    setContentType(null);
    setProductionStage(null);
    setVisibleOnDesk(true);
    setAttachment(null);
    momentKeyRef.current = crypto.randomUUID();
    // `subject` is "" (no day given — the header's "+ New" door) or a
    // "YYYY-MM-DD" string (a specific calendar cell). Either way it is a
    // single day to start; the end field stays collapsed until asked for.
    setStartsOn(subject as string);
    setEndsOn('');
    setShowEndDate(false);
    if (prefill) applyArticle(prefill);
    // `prefill` is read once per subject, like every other starting value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, existing]);

  /* Fill from an editorial article. Overwrites what an article decides (its
     title, link, type and date when it has one) and leaves the operator's
     notes and attachment alone. */
  function applyArticle(next: CalendarMomentPrefill): void {
    setTitle(next.title);
    setUrl(next.url);
    setContentType(next.contentType);
    if (next.startsOn) {
      setStartsOn(next.startsOn);
      setEndsOn('');
      setShowEndDate(false);
    }
    if (next.url) setDetailsOpen(true);
  }

  // Stated inline and used to disable submit, never surfaced as a toast after
  // the fact — a rule the person can break should say so while they are
  // breaking it, not after they have committed.
  const datesInvalid = Boolean(startsOn && endsOn && endsOn < startsOn);
  const canSubmit =
    title.trim().length > 0 &&
    Boolean(startsOn) &&
    !datesInvalid &&
    !create.isPending &&
    !update.isPending;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const uploaded = await uploadAttachment.mutateAsync({
        momentKey: momentKeyRef.current,
        file,
      });
      setAttachment(uploaded);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not attach that file.');
    }
  };

  const handleRemoveAttachment = (): void => {
    if (attachment) removeAttachment.mutate(attachment.path);
    setAttachment(null);
  };

  const handleOpenAttachment = async (): Promise<void> => {
    if (!attachment) return;
    const signedUrl = await getAttachmentUrl(attachment.path);
    if (signedUrl) window.open(signedUrl, '_blank', 'noopener,noreferrer');
    else toast.error('Could not open that file.');
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!canSubmit) return;
    const payload = {
      title,
      notes,
      url,
      startsOn: startsOn || null,
      endsOn: showEndDate ? endsOn || null : null,
      contentType,
      productionStage,
      visibleOnDesk,
      attachment,
    };
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, ...payload });
        toast.success('Moment updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Added to the calendar');
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the moment.');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!existing) return;
    try {
      await remove.mutateAsync(existing.id);
      toast.success('Moment removed');
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove the moment.');
    }
  };

  return (
    <>
      <Dialog open={subject !== null} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{existing ? 'Edit moment' : 'Add to the calendar'}</DialogTitle>
              <DialogDescription>
                Anything with a date that isn&apos;t a Mora post — a blog going live, a filming day,
                a launch.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              {existing ? null : <MomentArticlePicker onPick={applyArticle} />}
              <div className="space-y-1.5">
                <Label className={FIELD_LABEL} htmlFor="moment-title">
                  What is it
                </Label>
                <Input
                  id="moment-title"
                  autoFocus
                  autoComplete="off"
                  maxLength={200}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Q3 blog goes live"
                />
              </div>

              {/*
               * Type and Date share a row — two short, unrelated-length
               * controls that each used to claim a full-width row of their
               * own. Founder, 2026-09-16: "maybe type and date can be on the
               * same row as two columns to save some space here."
               *
               * 🔴 ONE "Date" FIELD, NOT A FORCED Starts/Ends PAIR — a range
               * is the exception ("12th Annual Energy Symposium") and a
               * single day is the rule ("Q3 blog goes live"), so the form
               * defaults to the common case and only splits when asked.
               * Modeled loosely on Google Calendar's own creation flow (one
               * date field, a range revealed by explicit action) rather than
               * inventing a new grammar — this product plans content, not
               * meeting times, so there's no time-of-day here to match.
               */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className={FIELD_LABEL} htmlFor="moment-type">
                    Type <span className="font-normal text-muted-foreground">(optional)</span>
                  </Label>
                  <Select
                    value={contentType ?? NO_TYPE}
                    onValueChange={(value) => {
                      const next = isDeskContentType(value) ? value : null;
                      setContentType(next);
                      // Status only makes sense once there's a kind of work
                      // to track progress on — clear it rather than submit a
                      // hidden value for a field the operator can no longer see.
                      if (!next) setProductionStage(null);
                    }}
                  >
                    <SelectTrigger id="moment-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* First, and the default. Not saying is a real answer. */}
                      <SelectItem value={NO_TYPE}>Not set</SelectItem>
                      {DESK_CONTENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {DESK_CONTENT_TYPE_LABEL[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className={FIELD_LABEL} htmlFor="moment-date">
                    {showEndDate ? 'Starts' : 'Date'}
                  </Label>
                  <div className="flex items-center gap-2">
                    <DatePicker
                      id="moment-date"
                      value={fromInput(startsOn)}
                      onChange={(value: Date | undefined) => {
                        const next = toInput(value);
                        setStartsOn(next);
                        // A start moved past the end is almost always someone
                        // rescheduling a moment, so carry the end with it
                        // rather than leaving an invalid range for them to
                        // notice and repair.
                        if (next && endsOn && endsOn < next) setEndsOn(next);
                      }}
                      placeholder="Pick a date"
                      ariaLabel={showEndDate ? 'Start date' : 'Date'}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {showEndDate ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className={FIELD_LABEL}>Ends</Label>
                    <button
                      type="button"
                      onClick={() => {
                        setEndsOn('');
                        setShowEndDate(false);
                      }}
                      className="text-detail text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Remove
                    </button>
                  </div>
                  <DatePicker
                    value={fromInput(endsOn)}
                    onChange={(value: Date | undefined) => setEndsOn(toInput(value))}
                    placeholder="Same day"
                    ariaLabel="End date"
                  />
                </div>
              ) : (
                startsOn && (
                  <button
                    type="button"
                    onClick={() => setShowEndDate(true)}
                    className="text-caption text-muted-foreground transition-colors hover:text-foreground"
                  >
                    + Add an end date
                  </button>
                )
              )}
              {datesInvalid && (
                <p className="text-detail text-destructive">
                  The end date is before the start date.
                </p>
              )}

              {/*
               * 🔴 PROGRESSIVE DISCLOSURE HERE, AND PRIMARY ON THE DESK — the
               * same field, two jobs, and the difference is deliberate.
               *
               * Authoring a moment, the answer to "what and when" is the whole
               * point; the link is something you paste once and rarely reread,
               * so it sits behind a disclosure with the notes, matching
               * `DeskItemPanel`'s "More details" pattern rather than inventing
               * a second one.
               *
               * On the CLIENT desk the same URL must stay primary and must NOT
               * be collapsed — `DeskItemPanel` already excludes `referenceUrl`
               * from its disclosure for a `page`, because there "it is the
               * entire thing the reviewer needs". A field's prominence follows
               * the reader's task, not the field's type.
               */}
              <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 rounded-lg py-2 text-caption text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  More details
                  <CaretDownIcon
                    className="size-4 transition-transform group-data-[state=open]:rotate-180"
                    weight="regular"
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-1">
                  {/*
                   * DYNAMIC ON TYPE, ON PURPOSE. "How far along is the work"
                   * is a real question for a blog, video, article, graphic,
                   * document or page — it has no answer for a bare event
                   * ("12th Annual Energy Symposium") or an unset type, so the
                   * field only appears once a type says there is work to
                   * track. Options come from `PRODUCTION_STAGE_LABEL`
                   * (`src/lib/desk/stage.ts`) — the SAME vocabulary and
                   * wording the Client Desk tab already renders via
                   * `deskStageLabel`, not a second one invented here.
                   */}
                  {contentType && (
                    <div className="space-y-1.5">
                      <Label className={FIELD_LABEL} htmlFor="moment-status">
                        Status <span className="font-normal text-muted-foreground">(optional)</span>
                      </Label>
                      <Select
                        value={productionStage ?? NO_STAGE}
                        onValueChange={(value) =>
                          setProductionStage(
                            (PRODUCTION_STAGES as readonly string[]).includes(value)
                              ? (value as ProductionStage)
                              : null,
                          )
                        }
                      >
                        <SelectTrigger id="moment-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_STAGE}>Not set</SelectItem>
                          {PRODUCTION_STAGES.map((stage) => (
                            <SelectItem key={stage} value={stage}>
                              {PRODUCTION_STAGE_LABEL[stage]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      {/* `spacing="none"` — the primitive's default `mb-2 block`
                          is correct for a label stacked above its field, but it
                          pushed this one out of vertical center against the
                          tooltip icon beside it (Label's own margin-bottom
                          enlarges its flex-item box, so `items-center` centers
                          THAT box while the visible text sits flush at its top).
                          Founder, 2026-09-16: "the information tooltip icon
                          doesn't look correctly centered here." */}
                      <Label className={FIELD_LABEL} htmlFor="moment-url" spacing="none">
                        Preview link
                      </Label>
                      {/* A label, not a promise — this is one URL doing two
                          jobs (a staging link before the thing is public, the
                          live page after), and no single field name says both
                          without a sentence beside it. Founder, 2026-09-16:
                          "the purpose of that URL is supposed to be something
                          to review... it should be like a preview link." */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="What is a preview link?"
                            className="shrink-0 rounded-sm text-muted-foreground/50 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <InfoIcon className="size-3.5" weight="regular" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          Anything useful to review this by — a staging or draft link before it is
                          public, or the live page once it is. Optional, and fine to add later.
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      id="moment-url"
                      type="url"
                      inputMode="url"
                      autoComplete="url"
                      value={url}
                      onChange={(event) => setUrl(event.target.value)}
                      // Generic on purpose, and not blog-specific — a moment
                      // can be a video, a page, a document, or an event with
                      // no content at all. A real domain here (this field
                      // used to show one workspace's own URL as the example)
                      // also reads as a leftover from someone else's account
                      // the moment a different workspace opens this dialog.
                      placeholder="https://your-site.com"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className={FIELD_LABEL} htmlFor="moment-notes">
                      Notes
                    </Label>
                    <Textarea
                      id="moment-notes"
                      rows={3}
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      placeholder="Call time, address, who's running it…"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className={FIELD_LABEL} htmlFor="moment-attachment">
                      Attachment{' '}
                      <span className="font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    {attachment ? (
                      <div
                        className={cn(
                          'flex items-center gap-2 rounded-lg border border-border/50 px-2.5 py-2',
                          ROW_HOVER,
                        )}
                      >
                        <span className={THUMBNAIL_SQUARE}>
                          <PaperclipIcon
                            className="size-3.5 text-muted-foreground/65"
                            weight="regular"
                          />
                        </span>
                        <button
                          type="button"
                          id="moment-attachment"
                          onClick={() => void handleOpenAttachment()}
                          className={cn(
                            ITEM_TITLE,
                            'min-w-0 flex-1 truncate text-left hover:underline',
                          )}
                        >
                          {attachment.name}
                        </button>
                        <Button
                          type="button"
                          variant="flat"
                          size="icon-sm"
                          onClick={handleRemoveAttachment}
                          aria-label={`Remove ${attachment.name}`}
                        >
                          <XIcon className="size-3.5" weight="regular" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={(event) => void handleFileChange(event)}
                        />
                        <Button
                          type="button"
                          id="moment-attachment"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={uploadAttachment.isPending}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <PaperclipIcon className="size-3.5" weight="regular" />
                          {uploadAttachment.isPending ? 'Uploading…' : 'Attach file'}
                        </Button>
                      </>
                    )}
                  </div>

                  <div className={SETTINGS_ROW}>
                    <div className="min-w-0 flex-1">
                      <p className={SETTINGS_ROW_TITLE}>Show on the client desk</p>
                      <p className={SETTINGS_ROW_DESCRIPTION}>
                        Visible to whoever reviews this workspace&apos;s Content Desk. Off keeps it
                        internal-only.
                      </p>
                    </div>
                    <Switch
                      size="sm"
                      checked={visibleOnDesk}
                      onCheckedChange={setVisibleOnDesk}
                      aria-label="Show on the client desk"
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <DialogFooter className="mt-6">
              {existing && (
                /* `mr-auto`: destroying and saving are not peers, so delete
                   sits at the far left rather than beside the confirm where a
                   fast hand lands on it. */
                <Button
                  type="button"
                  variant="flat"
                  size="sm"
                  className="mr-auto text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(true)}
                  disabled={remove.isPending}
                >
                  Delete
                </Button>
              )}
              <Button type="button" variant="flat" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!canSubmit}>
                {create.isPending || update.isPending
                  ? 'Saving…'
                  : existing
                    ? 'Save'
                    : 'Add to calendar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* The repo's own Confirm, never `window.confirm` — a native modal blocks
          every subsequent browser event, which freezes automated verification
          of this very dialog. */}
      <Confirm
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Remove this moment?"
        description="It disappears from the calendar. Nothing else is affected."
        confirmLabel="Remove moment"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </>
  );
}
